import { useState, useEffect, useCallback } from "react";
import { 
  Truck, Plus, Search, Loader2, Eye, FileText, Receipt, 
  ArrowLeftRight, Download, Trash2, CheckCircle, Package,
  MapPin, User, Calendar, Clock
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription
} from "../components/ui/dialog";
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from "../components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { authFetch } from "../services/authService";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DDT({ user }) {
  const [ddtList, setDdtList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [selectedDDT, setSelectedDDT] = useState(null);
  const [showReturnDialog, setShowReturnDialog] = useState(null);
  const [returnItems, setReturnItems] = useState([]);
  
  const [formData, setFormData] = useState({
    customer_name: "",
    customer_fiscal_code: "",
    customer_vat_number: "",
    customer_address: "",
    customer_city: "",
    customer_province: "",
    customer_cap: "",
    destination_address: "",
    destination_city: "",
    transport_reason: "Vendita",
    transport_by: "Mittente",
    carrier_name: "",
    vehicle_plate: "",
    items: [{ item_code: "", description: "", quantity: 1, unit_price: 0, vat_rate: 22, unit_of_measure: "PZ" }],
    notes: "",
    weight_kg: 0,
    packages: 1
  });

  const fetchDDT = useCallback(async () => {
    try {
      setLoading(true);
      const res = await authFetch(`${API}/ddt`, { });
      if (res.ok) {
        setDdtList(await res.json());
      }
    } catch (error) {
      console.error("Error fetching DDT:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDDT();
  }, [fetchDDT]);

  const handleSubmit = async () => {
    if (!formData.customer_name || formData.items.length === 0) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }

    try {
      const res = await authFetch(`${API}/ddt`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        toast.success("DDT creato con successo");
        setShowNewDialog(false);
        resetForm();
        fetchDDT();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Errore nella creazione");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    }
  };

  const handleIssue = async (ddtId) => {
    try {
      const res = await authFetch(`${API}/ddt/${ddtId}/issue`, {
        method: "PATCH"
      });

      if (res.ok) {
        toast.success("DDT emesso");
        fetchDDT();
        if (selectedDDT?.ddt_id === ddtId) {
          setSelectedDDT({ ...selectedDDT, status: "issued" });
        }
      }
    } catch (error) {
      toast.error("Errore");
    }
  };

  const handleCreateInvoice = async (ddtId) => {
    try {
      const res = await authFetch(`${API}/ddt/${ddtId}/create-invoice`, {
        method: "POST"
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(`Fattura ${data.invoice_number} creata`);
        fetchDDT();
      }
    } catch (error) {
      toast.error("Errore nella creazione fattura");
    }
  };

  const handleCreateReturn = async () => {
    if (!showReturnDialog || returnItems.length === 0) return;

    try {
      const res = await authFetch(`${API}/ddt/${showReturnDialog.ddt_id}/create-return`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: returnItems })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(`DDT di Reso ${data.ddt_number} creato`);
        setShowReturnDialog(null);
        setReturnItems([]);
        fetchDDT();
      }
    } catch (error) {
      toast.error("Errore");
    }
  };

  const handleDelete = async (ddtId) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo DDT?")) return;

    try {
      const res = await authFetch(`${API}/ddt/${ddtId}`, {
        method: "DELETE"
      });

      if (res.ok) {
        toast.success("DDT eliminato");
        fetchDDT();
        setSelectedDDT(null);
      }
    } catch (error) {
      toast.error("Errore");
    }
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { item_code: "", description: "", quantity: 1, unit_price: 0, vat_rate: 22, unit_of_measure: "PZ" }]
    }));
  };

  const removeItem = (index) => {
    if (formData.items.length <= 1) return;
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const updateItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === index ? { ...item, [field]: value } : item)
    }));
  };

  const resetForm = () => {
    setFormData({
      customer_name: "",
      customer_fiscal_code: "",
      customer_vat_number: "",
      customer_address: "",
      customer_city: "",
      customer_province: "",
      customer_cap: "",
      destination_address: "",
      destination_city: "",
      transport_reason: "Vendita",
      transport_by: "Mittente",
      carrier_name: "",
      vehicle_plate: "",
      items: [{ item_code: "", description: "", quantity: 1, unit_price: 0, vat_rate: 22, unit_of_measure: "PZ" }],
      notes: "",
      weight_kg: 0,
      packages: 1
    });
  };

  const calculateTotals = () => {
    let subtotal = 0;
    let vat = 0;
    formData.items.forEach(item => {
      const itemSubtotal = (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0);
      subtotal += itemSubtotal;
      vat += itemSubtotal * ((parseFloat(item.vat_rate) || 22) / 100);
    });
    return { subtotal: subtotal.toFixed(2), vat: vat.toFixed(2), total: (subtotal + vat).toFixed(2) };
  };

  const totals = calculateTotals();

  const statusColors = {
    draft: "bg-zinc-500/20 text-zinc-400",
    issued: "bg-blue-500/20 text-blue-400",
    invoiced: "bg-green-500/20 text-green-400",
    returned: "bg-red-500/20 text-red-400"
  };

  const statusLabels = {
    draft: "Bozza",
    issued: "Emesso",
    invoiced: "Fatturato",
    returned: "Reso"
  };

  const filteredDDT = ddtList.filter(d =>
    d.ddt_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    d.customer_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="ddt-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white uppercase font-['Barlow_Condensed'] tracking-wider flex items-center gap-2">
            <Truck className="w-6 h-6 text-primary" />
            Documenti di Trasporto
          </h1>
          <p className="text-zinc-400 text-sm">Gestione DDT con generazione fatture e resi</p>
        </div>
        
        <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
          <DialogTrigger asChild>
            <Button data-testid="btn-nuovo-ddt">
              <Plus className="w-4 h-4 mr-2" />
              Nuovo DDT
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white uppercase font-['Barlow_Condensed']">Nuovo DDT</DialogTitle>
              <DialogDescription className="text-zinc-400">Crea un nuovo documento di trasporto</DialogDescription>
            </DialogHeader>
            <div className="space-y-6 py-4">
              {/* Cliente */}
              <div className="border border-zinc-800 rounded p-4">
                <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <User className="w-4 h-4 text-primary" />
                  Destinatario
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-zinc-400">Ragione Sociale *</label>
                    <Input
                      value={formData.customer_name}
                      onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                      className="bg-zinc-800 border-zinc-700"
                      placeholder="Nome cliente"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-zinc-400">P.IVA</label>
                    <Input
                      value={formData.customer_vat_number}
                      onChange={(e) => setFormData({...formData, customer_vat_number: e.target.value})}
                      className="bg-zinc-800 border-zinc-700"
                      placeholder="IT12345678901"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-zinc-400">Codice Fiscale</label>
                    <Input
                      value={formData.customer_fiscal_code}
                      onChange={(e) => setFormData({...formData, customer_fiscal_code: e.target.value})}
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-zinc-400">Indirizzo</label>
                    <Input
                      value={formData.customer_address}
                      onChange={(e) => setFormData({...formData, customer_address: e.target.value})}
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-zinc-400">Città</label>
                    <Input
                      value={formData.customer_city}
                      onChange={(e) => setFormData({...formData, customer_city: e.target.value})}
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-sm text-zinc-400">Prov.</label>
                      <Input
                        value={formData.customer_province}
                        onChange={(e) => setFormData({...formData, customer_province: e.target.value.toUpperCase()})}
                        className="bg-zinc-800 border-zinc-700 uppercase"
                        maxLength={2}
                      />
                    </div>
                    <div>
                      <label className="text-sm text-zinc-400">CAP</label>
                      <Input
                        value={formData.customer_cap}
                        onChange={(e) => setFormData({...formData, customer_cap: e.target.value})}
                        className="bg-zinc-800 border-zinc-700"
                        maxLength={5}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Trasporto */}
              <div className="border border-zinc-800 rounded p-4">
                <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                  <Truck className="w-4 h-4 text-primary" />
                  Dati Trasporto
                </h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm text-zinc-400">Causale Trasporto</label>
                    <Select value={formData.transport_reason} onValueChange={(v) => setFormData({...formData, transport_reason: v})}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Vendita">Vendita</SelectItem>
                        <SelectItem value="Conto visione">Conto visione</SelectItem>
                        <SelectItem value="Conto lavorazione">Conto lavorazione</SelectItem>
                        <SelectItem value="Riparazione">Riparazione</SelectItem>
                        <SelectItem value="Reso">Reso</SelectItem>
                        <SelectItem value="Omaggio">Omaggio</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm text-zinc-400">Trasporto a cura</label>
                    <Select value={formData.transport_by} onValueChange={(v) => setFormData({...formData, transport_by: v})}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-700">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Mittente">Mittente</SelectItem>
                        <SelectItem value="Destinatario">Destinatario</SelectItem>
                        <SelectItem value="Vettore">Vettore</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm text-zinc-400">Vettore</label>
                    <Input
                      value={formData.carrier_name}
                      onChange={(e) => setFormData({...formData, carrier_name: e.target.value})}
                      className="bg-zinc-800 border-zinc-700"
                      placeholder="Nome vettore"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-zinc-400">Colli</label>
                    <Input
                      type="number"
                      value={formData.packages}
                      onChange={(e) => setFormData({...formData, packages: parseInt(e.target.value) || 1})}
                      className="bg-zinc-800 border-zinc-700"
                      min={1}
                    />
                  </div>
                  <div>
                    <label className="text-sm text-zinc-400">Peso (Kg)</label>
                    <Input
                      type="number"
                      step="0.1"
                      value={formData.weight_kg}
                      onChange={(e) => setFormData({...formData, weight_kg: parseFloat(e.target.value) || 0})}
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-zinc-400">Targa Mezzo</label>
                    <Input
                      value={formData.vehicle_plate}
                      onChange={(e) => setFormData({...formData, vehicle_plate: e.target.value.toUpperCase()})}
                      className="bg-zinc-800 border-zinc-700 uppercase"
                    />
                  </div>
                </div>
              </div>

              {/* Articoli */}
              <div className="border border-zinc-800 rounded p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-white font-semibold flex items-center gap-2">
                    <Package className="w-4 h-4 text-primary" />
                    Articoli
                  </h4>
                  <Button variant="outline" size="sm" onClick={addItem}>
                    <Plus className="w-4 h-4 mr-1" /> Aggiungi
                  </Button>
                </div>
                <div className="space-y-2">
                  {formData.items.map((item, index) => (
                    <div key={index} className="grid grid-cols-12 gap-2 items-end bg-zinc-950/50 p-2 rounded">
                      <div className="col-span-2">
                        <label className="text-xs text-zinc-500">Codice</label>
                        <Input
                          value={item.item_code}
                          onChange={(e) => updateItem(index, "item_code", e.target.value)}
                          className="bg-zinc-800 border-zinc-700 h-8 text-sm"
                        />
                      </div>
                      <div className="col-span-4">
                        <label className="text-xs text-zinc-500">Descrizione</label>
                        <Input
                          value={item.description}
                          onChange={(e) => updateItem(index, "description", e.target.value)}
                          className="bg-zinc-800 border-zinc-700 h-8 text-sm"
                        />
                      </div>
                      <div className="col-span-1">
                        <label className="text-xs text-zinc-500">Qtà</label>
                        <Input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, "quantity", parseInt(e.target.value) || 1)}
                          className="bg-zinc-800 border-zinc-700 h-8 text-sm"
                          min={1}
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="text-xs text-zinc-500">Prezzo</label>
                        <Input
                          type="number"
                          step="0.01"
                          value={item.unit_price}
                          onChange={(e) => updateItem(index, "unit_price", parseFloat(e.target.value) || 0)}
                          className="bg-zinc-800 border-zinc-700 h-8 text-sm"
                        />
                      </div>
                      <div className="col-span-1">
                        <label className="text-xs text-zinc-500">IVA%</label>
                        <Input
                          type="number"
                          value={item.vat_rate}
                          onChange={(e) => updateItem(index, "vat_rate", parseFloat(e.target.value) || 22)}
                          className="bg-zinc-800 border-zinc-700 h-8 text-sm"
                        />
                      </div>
                      <div className="col-span-1">
                        <label className="text-xs text-zinc-500">UM</label>
                        <Input
                          value={item.unit_of_measure}
                          onChange={(e) => updateItem(index, "unit_of_measure", e.target.value)}
                          className="bg-zinc-800 border-zinc-700 h-8 text-sm"
                        />
                      </div>
                      <div className="col-span-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-400"
                          onClick={() => removeItem(index)}
                          disabled={formData.items.length <= 1}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex justify-end mt-4 gap-6 text-sm">
                  <span className="text-zinc-400">Imponibile: <strong className="text-white">€{totals.subtotal}</strong></span>
                  <span className="text-zinc-400">IVA: <strong className="text-white">€{totals.vat}</strong></span>
                  <span className="text-zinc-400">Totale: <strong className="text-primary text-lg">€{totals.total}</strong></span>
                </div>
              </div>

              {/* Note */}
              <div>
                <label className="text-sm text-zinc-400">Note</label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  className="bg-zinc-800 border-zinc-700"
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowNewDialog(false)}>Annulla</Button>
              <Button onClick={handleSubmit} data-testid="btn-salva-ddt">Crea DDT</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <FileText className="w-8 h-8 text-primary" />
              <div>
                <p className="text-zinc-400 text-sm">Totale DDT</p>
                <p className="text-2xl font-bold text-white">{ddtList.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-zinc-400" />
              <div>
                <p className="text-zinc-400 text-sm">Bozze</p>
                <p className="text-2xl font-bold text-white">{ddtList.filter(d => d.status === "draft").length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-blue-400" />
              <div>
                <p className="text-zinc-400 text-sm">Emessi</p>
                <p className="text-2xl font-bold text-white">{ddtList.filter(d => d.status === "issued").length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Receipt className="w-8 h-8 text-green-400" />
              <div>
                <p className="text-zinc-400 text-sm">Fatturati</p>
                <p className="text-2xl font-bold text-white">{ddtList.filter(d => d.status === "invoiced").length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <Input
          type="text"
          placeholder="Cerca per numero o cliente..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-zinc-900/50 border-zinc-800"
        />
      </div>

      {/* DDT List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : filteredDDT.length === 0 ? (
        <div className="text-center py-12">
          <Truck className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
          <p className="text-zinc-500">Nessun DDT trovato</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left py-3 px-4 text-zinc-400 font-medium">Numero</th>
                <th className="text-left py-3 px-4 text-zinc-400 font-medium">Data</th>
                <th className="text-left py-3 px-4 text-zinc-400 font-medium">Cliente</th>
                <th className="text-left py-3 px-4 text-zinc-400 font-medium">Causale</th>
                <th className="text-right py-3 px-4 text-zinc-400 font-medium">Totale</th>
                <th className="text-center py-3 px-4 text-zinc-400 font-medium">Stato</th>
                <th className="text-right py-3 px-4 text-zinc-400 font-medium">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {filteredDDT.map((ddt) => (
                <tr 
                  key={ddt.ddt_id} 
                  className="border-b border-zinc-800/50 hover:bg-zinc-900/30 cursor-pointer"
                  onClick={() => setSelectedDDT(ddt)}
                >
                  <td className="py-3 px-4">
                    <span className="text-primary font-mono font-bold">{ddt.ddt_number}</span>
                    {ddt.is_return && <Badge className="ml-2 bg-red-500/20 text-red-400 text-xs">RESO</Badge>}
                  </td>
                  <td className="py-3 px-4 text-zinc-400">
                    {new Date(ddt.created_at).toLocaleDateString("it-IT")}
                  </td>
                  <td className="py-3 px-4 text-white">{ddt.customer_name}</td>
                  <td className="py-3 px-4 text-zinc-400">{ddt.transport_reason}</td>
                  <td className="py-3 px-4 text-right text-white font-mono">€{ddt.total?.toFixed(2)}</td>
                  <td className="py-3 px-4 text-center">
                    <Badge className={statusColors[ddt.status]}>
                      {statusLabels[ddt.status]}
                    </Badge>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                      {ddt.status === "draft" && (
                        <>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleIssue(ddt.ddt_id)}>
                            <CheckCircle className="w-4 h-4 text-blue-400" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400" onClick={() => handleDelete(ddt.ddt_id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      {ddt.status === "issued" && !ddt.is_return && (
                        <>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleCreateInvoice(ddt.ddt_id)} title="Crea Fattura">
                            <Receipt className="w-4 h-4 text-green-400" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setShowReturnDialog(ddt); setReturnItems(ddt.items.map(i => ({ item_code: i.item_code, quantity: i.quantity }))); }} title="DDT di Reso">
                            <ArrowLeftRight className="w-4 h-4 text-yellow-400" />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Return Dialog */}
      <Dialog open={!!showReturnDialog} onOpenChange={() => setShowReturnDialog(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-white">DDT di Reso</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Seleziona gli articoli da restituire dal DDT {showReturnDialog?.ddt_number}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-4">
            {showReturnDialog?.items?.map((item, idx) => (
              <div key={idx} className="flex items-center gap-4 p-2 bg-zinc-800 rounded">
                <div className="flex-1">
                  <p className="text-white text-sm">{item.description}</p>
                  <p className="text-zinc-400 text-xs">Max: {item.quantity}</p>
                </div>
                <Input
                  type="number"
                  value={returnItems.find(r => r.item_code === item.item_code)?.quantity || 0}
                  onChange={(e) => {
                    const qty = Math.min(parseInt(e.target.value) || 0, item.quantity);
                    setReturnItems(prev => {
                      const existing = prev.find(r => r.item_code === item.item_code);
                      if (existing) {
                        return prev.map(r => r.item_code === item.item_code ? { ...r, quantity: qty } : r);
                      }
                      return [...prev, { item_code: item.item_code, quantity: qty }];
                    });
                  }}
                  className="w-20 bg-zinc-700 border-zinc-600"
                  min={0}
                  max={item.quantity}
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowReturnDialog(null)}>Annulla</Button>
            <Button onClick={handleCreateReturn}>Crea DDT Reso</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!selectedDDT} onOpenChange={() => setSelectedDDT(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Truck className="w-5 h-5 text-primary" />
              DDT {selectedDDT?.ddt_number}
              <Badge className={statusColors[selectedDDT?.status]}>{statusLabels[selectedDDT?.status]}</Badge>
            </DialogTitle>
          </DialogHeader>
          {selectedDDT && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-zinc-400 text-sm">Cliente</p>
                  <p className="text-white font-semibold">{selectedDDT.customer_name}</p>
                  <p className="text-zinc-400 text-sm">{selectedDDT.customer_address}</p>
                  <p className="text-zinc-400 text-sm">{selectedDDT.customer_city} ({selectedDDT.customer_province})</p>
                </div>
                <div>
                  <p className="text-zinc-400 text-sm">Trasporto</p>
                  <p className="text-white">{selectedDDT.transport_reason}</p>
                  <p className="text-zinc-400 text-sm">A cura: {selectedDDT.transport_by}</p>
                  {selectedDDT.carrier_name && <p className="text-zinc-400 text-sm">Vettore: {selectedDDT.carrier_name}</p>}
                </div>
              </div>
              <div className="border-t border-zinc-800 pt-4">
                <p className="text-zinc-400 text-sm mb-2">Articoli</p>
                <div className="space-y-1">
                  {selectedDDT.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm p-2 bg-zinc-800/50 rounded">
                      <span className="text-white">{item.description}</span>
                      <span className="text-zinc-400">x{item.quantity} @ €{item.unit_price?.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-end gap-4 text-sm border-t border-zinc-800 pt-4">
                <span className="text-zinc-400">Imponibile: <strong className="text-white">€{selectedDDT.subtotal?.toFixed(2)}</strong></span>
                <span className="text-zinc-400">IVA: <strong className="text-white">€{selectedDDT.vat_total?.toFixed(2)}</strong></span>
                <span className="text-zinc-400">Totale: <strong className="text-primary text-lg">€{selectedDDT.total?.toFixed(2)}</strong></span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
